import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { useEffect, useTransition } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import z from "zod";
import { Image } from "@/components/image";
import { OffersTable } from "@/components/tables/offers";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	isKnownPaymentProvider,
	PaymentProviderInfo,
} from "@/utils/payment-provider-info";
import type { OffersTableQuery } from "./__generated__/OffersTableQuery.graphql";
import type { ofertas_table$key } from "./__generated__/ofertas_table.graphql";
import type { ofertasQuery } from "./__generated__/ofertasQuery.graphql";

const defaultValues = {
	page: 1,
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/monetizacao/ofertas",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			page: z.number().default(1),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate({ from: Route.fullPath });
	const data = useLazyLoadQuery<ofertasQuery>(
		graphql`
    query ofertasQuery($slug: String!, $first: Int!, $after: String) {
      club(slug: $slug) {
        ...ofertas_table @arguments(first: $first, after: $after)

        integrations {
          ... on PaymentIntegration {
            id
            provider
          }

          __typename
        }
      }
    }
  `,
		{
			slug: club.slug,
			first: 15,
			after: page > 1 ? offsetToCursor((page - 1) * 15 - 1) : undefined,
		},
	);

	const [offers, refetch] = useRefetchableFragment<
		OffersTableQuery,
		ofertas_table$key
	>(
		graphql`
    fragment ofertas_table on Club @refetchable(queryName: "OffersTableQuery")
    @argumentDefinitions(
      first: { type: "Int", defaultValue: 15 }
      after: { type: "String", defaultValue: null }
    )
    {
      offers(first: $first, after: $after) {
        edges {
          node {
            id
            productId
            productName
            enabled
            integration {
              provider
            }
            accessGroup {
              accessGroup {
                courses {
                  course {
                    name
                  }
                }
                spaces {
                  name
                  icon
                }
              }
            }
          }
          cursor
        }
        pageInfo {
					hasNextPage
					hasPreviousPage
					totalCount
					startCursor
					endCursor
				}
      }
    }
  `,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: 15,
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);

	return (
		<div className="p-6 flex flex-col gap-6">
			<div className="flex flex-col items-start justify-start p-6 gap-6 overflow-hidden rounded-md border">
				<span className="font-semibold">Checkouts Integrados</span>

				<div className="flex flex-row gap-3 items-center justify-start">
					{data.club.integrations
						.filter(
							(
								it,
							): it is Extract<
								typeof it,
								{ readonly __typename: "PaymentIntegration" }
							> & {
								readonly provider: keyof typeof PaymentProviderInfo;
							} =>
								it.__typename === "PaymentIntegration" &&
								it.provider !== undefined &&
								isKnownPaymentProvider(it.provider),
						)
						.map((it) => (
							<Tooltip key={it.provider}>
								<TooltipTrigger asChild>
									<button
										type="button"
										onClick={() =>
											navigate({
												to: "/c/$club/configuracoes/integracoes/checkout/$id",
												params: { club: club.slug, id: it.id },
											})
										}
										className="cursor-pointer hover:opacity-80 transition-opacity"
									>
										<Image
											src={PaymentProviderInfo[it.provider]?.logo}
											className="rounded-md size-12"
										/>
									</button>
								</TooltipTrigger>

								<TooltipContent>
									{PaymentProviderInfo[it.provider]?.name}
								</TooltipContent>
							</Tooltip>
						))}
				</div>
			</div>

			<OffersTable
				data={
					offers?.offers?.edges.filter(Boolean).map(({ node }) => ({
						id: node.id,
						enabled: node.enabled,
						productName: node.productName ?? "Oferta sem nome",
						productId: node.productId ?? "-",
						provider: node.integration?.provider || undefined,
						courses:
							node.accessGroup.accessGroup.courses
								.filter(Boolean)
								.map((c) => c.course) || [],
						spaces: node.accessGroup.accessGroup.spaces
							.filter(Boolean)
							.map((space) => ({ name: space.name, icon: space.icon ?? "" })),
					})) || []
				}
				isPending={isPending}
				totalCount={offers?.offers.pageInfo.totalCount || 0}
				hasNextPage={Boolean(offers?.offers.pageInfo.hasNextPage)}
				hasPreviousPage={Boolean(offers?.offers.pageInfo.hasPreviousPage)}
				loadNext={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
					})
				}
				loadPrevious={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
					})
				}
				startIndex={
					offers?.offers.edges[0]
						? cursorToOffset(offers?.offers.edges[0].cursor) + 1
						: 1
				}
				endIndex={
					offers?.offers.edges[offers?.offers.edges.length - 1]
						? cursorToOffset(
								offers?.offers.edges[offers?.offers.edges.length - 1].cursor,
							) + 1
						: 1
				}
			/>
		</div>
	);
}
